import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  listAllParametrosComDesc,
  parseStoredValue,
  updateParametroComDesc,
  type ParametroComDescItem,
} from '@/shared/api/controllers/parametrosComDesc';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { EntityViewProps } from '../../types';

const QUERY_KEY = ['configuracion', 'parametros-com-desc'] as const;

/**
 * Denormalizes the stored value for display: fraction → percentage.
 * E.g. 0.10 with TipoValor_PCD='Porcentaje' is shown as 10 in the input.
 * Input may arrive as a number OR as a Spanish comma-decimal string
 * (SharePoint stores these as text), so we parse first.
 */
function displayValue(
  raw: string | number | null | undefined,
  tipo: string | null | undefined,
): number {
  const n = parseStoredValue(raw);
  return tipo === 'Porcentaje' ? n * 100 : n;
}

function formatDisplay(
  raw: string | number | null | undefined,
  tipo: string | null | undefined,
): string {
  // A '-' tipo means "not applicable" (e.g. Regalos / Referidos have no
  // threshold Valor), so render a dash regardless of the stored number.
  if (raw == null || raw === '' || raw === '-' || tipo === '-') return '—';
  const v = displayValue(raw, tipo);
  const suffix = tipo === 'Porcentaje' ? ' %' : '';
  return `${v.toLocaleString('es-AR', { maximumFractionDigits: 4 })}${suffix}`;
}

export function ParametrosEntity({ search, filterValue, onStatus }: EntityViewProps) {
  const queryClient = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const scopedKey = useMemo(
    () => [...QUERY_KEY, activeCountry.code] as readonly unknown[],
    [activeCountry.code],
  );
  const { data: rows = [], isPending, isFetching, error, refetch } = useQuery({
    queryKey: scopedKey,
    queryFn: listAllParametrosComDesc,
    staleTime: 60_000,
  });
  const [editing, setEditing] = useState<ParametroComDescItem | null>(null);
  // Parametros are edit-only — `openCreate` is never raised for this entity
  // (ConfiguracionPage hides the + button). No create handler wiring needed.

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let sorted = [...rows].sort((a, b) =>
      (a.fields.Parametro_PCD ?? '').localeCompare(b.fields.Parametro_PCD ?? '', 'es'),
    );
    if (filterValue) {
      sorted = sorted.filter(
        (r) =>
          r.fields.TipoValor_PCD === filterValue ||
          r.fields.TipoValorAplicado_PCD === filterValue,
      );
    }
    if (!q) return sorted;
    return sorted.filter((r) => (r.fields.Parametro_PCD ?? '').toLowerCase().includes(q));
  }, [rows, search, filterValue]);

  const columns: CrudColumn<ParametroComDescItem>[] = [
    {
      header: 'Parámetro',
      render: (r) => (
        <span className="font-medium text-foreground">{r.fields.Parametro_PCD || '—'}</span>
      ),
    },
    {
      header: 'Tipo',
      render: (r) => <span className="text-muted-foreground">{r.fields.Tipo_PCD || '—'}</span>,
    },
    {
      header: 'Valor',
      render: (r) => formatDisplay(r.fields.Valor_PCD, r.fields.TipoValor_PCD),
    },
    {
      header: 'Valor aplicado',
      // Read the INTERNAL name — Graph never returns the display form.
      render: (r) =>
        formatDisplay(r.fields.ValorPorcentaje_PCD, r.fields.TipoValorAplicado_PCD),
    },
  ];

  const canEdit = useIsAdminTier();
  return (
    <>
      <CrudTable
        hideActions={!canEdit}
        rows={filtered}
        columns={columns}
        loading={isPending}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        emptyMessage={search ? `No hay resultados para "${search}".` : 'Sin parámetros.'}
        onEdit={(r) => setEditing(r)}
        isBackgroundFetching={isFetching}
      />

      {editing && (
        <ParametroEditDialog
          item={editing}
          open
          onOpenChange={(o) => !o && setEditing(null)}
          onSuccess={(msg) => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
            setEditing(null);
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            setEditing(null);
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}
    </>
  );
}

const schema = z.object({
  valor: z.coerce.number({ invalid_type_error: 'Valor inválido' }),
  valorAplicado: z.coerce.number({ invalid_type_error: 'Valor inválido' }),
});
type Values = z.infer<typeof schema>;

function ParametroEditDialog({
  item,
  open,
  onOpenChange,
  onSuccess,
  onError,
}: {
  item: ParametroComDescItem;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}) {
  const defaults: Values = {
    valor: displayValue(item.fields.Valor_PCD, item.fields.TipoValor_PCD),
    // Read the INTERNAL name — same quirk as the list columns above.
    valorAplicado: displayValue(
      item.fields.ValorPorcentaje_PCD,
      item.fields.TipoValorAplicado_PCD,
    ),
  };

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  useEffect(() => {
    if (open) form.reset(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item.id]);

  const mutation = useMutation({
    mutationFn: async (values: Values) =>
      updateParametroComDesc(item, {
        valor: values.valor,
        valorAplicado: values.valorAplicado,
      }),
    onSuccess: () => onSuccess('Parámetro actualizado correctamente.'),
    onError: (err) =>
      onError(err instanceof Error ? err.message : 'No se pudo actualizar el parámetro.'),
  });

  const onSubmit = form.handleSubmit((values) => mutation.mutate(values));

  // Params with `TipoValor_PCD === '-'` (Regalos de Vendedores, Referidos)
  // have no separate threshold — "Valor" and "Valor aplicado" hold the
  // same number, and consumers read only the applied one
  // (`ValorPorcentaje_PCD`). Show a single field bound to the applied
  // value; `valor` keeps its original value and round-trips on save, so
  // nothing reading `Valor_PCD` is affected. Avoids the trap of editing
  // "Valor" while the code reads "Valor aplicado".
  const isSingleValue = (item.fields.TipoValor_PCD ?? '') === '-';

  const watchedValor = form.watch('valor');
  const watchedValorAplicado = form.watch('valorAplicado');
  const missingFields: string[] = [];
  if (!isSingleValue && (watchedValor === undefined || Number.isNaN(watchedValor)))
    missingFields.push('Valor');
  if (watchedValorAplicado === undefined || Number.isNaN(watchedValorAplicado))
    missingFields.push(isSingleValue ? 'Valor' : 'Valor aplicado');
  const canSubmit = missingFields.length === 0;

  const suffixFor = (t: string | null | undefined) => (t === 'Porcentaje' ? '%' : '');

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-md"
        processing={mutation.isPending && 'Guardando cambios…'}
      >
        <DialogHeader>
          <DialogTitle>Editar parámetro</DialogTitle>
          <DialogDescription>
            {item.fields.Parametro_PCD ?? 'Parámetro'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden" noValidate>
          <DialogBody className="space-y-4">
            {/* Single-value params ('-' threshold) collapse to one field.
                It edits the applied value (`ValorPorcentaje_PCD`), which is
                what consumers read; `valor` round-trips untouched. */}
            {!isSingleValue && (
              <NumericField
                label="Valor"
                suffix={suffixFor(item.fields.TipoValor_PCD)}
                error={form.formState.errors.valor?.message}
                {...form.register('valor', { valueAsNumber: true })}
              />
            )}
            <NumericField
              label={isSingleValue ? 'Valor' : 'Valor aplicado'}
              suffix={suffixFor(item.fields.TipoValorAplicado_PCD)}
              error={form.formState.errors.valorAplicado?.message}
              {...form.register('valorAplicado', { valueAsNumber: true })}
            />
          </DialogBody>
          <DialogFooter>
            {missingFields.length > 0 && (
              <p className="mr-auto text-xs text-muted-foreground">
                Faltan completar: <span className="font-medium text-destructive">{missingFields.join(', ')}</span>
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !canSubmit}>
              Guardar cambios
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

import { forwardRef, type InputHTMLAttributes } from 'react';

type NumericFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  suffix?: string;
  error?: string;
};

const NumericField = forwardRef<HTMLInputElement, NumericFieldProps>(
  ({ label, suffix, error, id, name, ...props }, ref) => {
    const fieldId = id ?? name;
    return (
      <div className="space-y-1.5">
        <Label htmlFor={fieldId}>{label}</Label>
        <div className="relative">
          <Input
            ref={ref}
            id={fieldId}
            name={name}
            type="number"
            step="any"
            variant="filled"
            className="pr-10"
            {...props}
          />
          {suffix && (
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              {suffix}
            </span>
          )}
        </div>
        {error && <p className="text-xs font-medium text-destructive">{error}</p>}
      </div>
    );
  },
);
NumericField.displayName = 'NumericField';
